import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import SimOpsAPI, { getSimOpsErrorMessage } from 'api/ops/v2/SimOpsAPI'
import MenuClose from 'assets/icons/MenuClose.png'

import { refetchUserAllocations } from '../simOpsQueries'

interface ReassignPackDialogProps {
  open: boolean
  onClose: () => void
  userId: string
  packAssignmentId?: string
  packageName?: string
  onSuccess?: () => void
}

// Moves an INACTIVE pack to another receiver using either email or receiver ID.
function ReassignPackDialog({
  open,
  onClose,
  userId,
  packAssignmentId,
  packageName,
  onSuccess
}: ReassignPackDialogProps) {
  const queryClient = useQueryClient()
  const [receiverEmail, setReceiverEmail] = useState('')
  const [receiverId, setReceiverId] = useState('')

  useEffect(() => {
    if (open) {
      setReceiverEmail('')
      setReceiverId('')
    }
  }, [open])

  const { mutate, isLoading } = useMutation(SimOpsAPI.reassignPack, {
    onSuccess: () => {
      toast.success('Pack reassignment accepted')
      refetchUserAllocations(queryClient, userId)
      queryClient.refetchQueries({
        queryKey: ['getAllActiveInactiveOtpackUserPlans', userId]
      })
      onSuccess?.()
      onClose()
    },
    onError: (error) => {
      toast.error(getSimOpsErrorMessage(error, 'Failed to reassign pack'))
    }
  })

  const handleConfirm = () => {
    if (!packAssignmentId) {
      toast.error('This pack has no pack assignment ID and cannot be reassigned')
      return
    }
    const email = receiverEmail.trim()
    const id = receiverId.trim()

    if (!email && !id) {
      toast.error('Please provide either receiver email or receiver ID')
      return
    }

    if (email && id) {
      toast.error('Provide only one: receiver email or receiver ID')
      return
    }

    mutate({
      packAssignmentId,
      ...(email ? { receiverEmail: email } : {}),
      ...(id ? { receiverId: id } : {})
    })
  }

  return (
    <Dialog
      open={open}
      maxWidth='sm'
      fullWidth
      onClose={onClose}
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '20px',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        Reassign Pack
        <IconButton
          onClick={onClose}
          size='small'
          sx={{ marginRight: '-8px', marginTop: '-8px' }}
        >
          <img
            style={{ width: '20px', height: '20px' }}
            src={MenuClose}
            alt='Close'
          />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
          {packageName} · Pack assignment ID: {packAssignmentId || '—'}
        </Typography>
        <Typography variant='body1' sx={{ color: '#555', my: 2 }}>
          Only inactive packs can be reassigned. Provide either receiver email
          or receiver ID.
        </Typography>
        <TextField
          label='Receiver email'
          type='email'
          fullWidth
          margin='normal'
          value={receiverEmail}
          onChange={(e) => setReceiverEmail(e.target.value)}
          disabled={receiverId.trim().length > 0}
        />
        <TextField
          label='Receiver ID'
          fullWidth
          margin='normal'
          value={receiverId}
          onChange={(e) => setReceiverId(e.target.value)}
          disabled={receiverEmail.trim().length > 0}
        />
      </DialogContent>
      <DialogActions sx={{ padding: '16px' }}>
        <LoadingButton variant='text' onClick={onClose} disabled={isLoading}>
          Cancel
        </LoadingButton>
        <LoadingButton
          variant='contained'
          onClick={handleConfirm}
          loading={isLoading}
          disabled={
            !packAssignmentId ||
            (!receiverEmail.trim() && !receiverId.trim()) ||
            (!!receiverEmail.trim() && !!receiverId.trim())
          }
          loadingIndicator={<CircularProgress size={24} />}
          sx={{ paddingX: 4, fontWeight: 'bold' }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ReassignPackDialog
